import { useCallback, useState } from "react";
import { useGenerateSuggestedRoutes } from "@/api/orval";

// A suggested route as we use it on the map: a GeoJSON LineString Feature with
// the coverage stats the backend returns, plus a client-assigned id for
// selection. The backend returns a FeatureCollection with these properties.
export interface SuggestionFeature {
  type: "Feature";
  id: number;
  geometry: { type: "LineString"; coordinates: [number, number][] };
  properties: { distance_m: number; new_km: number; new_streets: number };
}

const DISTANCE_PRESETS = [5, 10, 15] as const;
const CANDIDATE_COUNT = 3;

interface UseSuggestedRoutesProps {
  workspaceId?: string;
}

export const useSuggestedRoutes = ({
  workspaceId,
}: UseSuggestedRoutesProps) => {
  const [distanceKm, setDistanceKm] = useState<number>(10);
  const [loop, setLoop] = useState(true);
  // The chosen start pin, or null to fall back to the map center at generate time.
  const [startPoint, setStartPoint] = useState<[number, number] | null>(null);
  // Human label for the start pin ("Dropped pin", a place name, "Current location").
  const [startLabel, setStartLabel] = useState<string | null>(null);
  // While true, the next map click sets the start pin instead of a route waypoint.
  const [pickingStart, setPickingStart] = useState(false);
  // Optional end pin. When set, routes run start to end and loop is ignored.
  const [endPoint, setEndPoint] = useState<[number, number] | null>(null);
  const [endLabel, setEndLabel] = useState<string | null>(null);
  const [pickingEnd, setPickingEnd] = useState(false);
  const [candidates, setCandidates] = useState<SuggestionFeature[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { mutateAsync, isPending } = useGenerateSuggestedRoutes();

  const generate = useCallback(
    async (fallbackCenter: [number, number]) => {
      if (!workspaceId) return;
      const [lng, lat] = startPoint ?? fallbackCenter;
      const res = await mutateAsync({
        pathParams: { workspaceId },
        data: {
          lng,
          lat,
          ...(endPoint ? { end_lng: endPoint[0], end_lat: endPoint[1] } : {}),
          distance_km: distanceKm,
          count: CANDIDATE_COUNT,
          loop,
          // Fresh seed each time so repeated Generate clicks explore different
          // routes (and the request is never served from any cache).
          seed: Math.floor(Math.random() * 2_000_000_000),
        },
      });
      const feats = (
        (res.features ?? []) as unknown as SuggestionFeature[]
      ).map((f, i) => ({ ...f, id: i }));
      setCandidates(feats);
      setSelectedId(null);
    },
    [workspaceId, startPoint, endPoint, distanceKm, loop, mutateAsync],
  );

  // Clear everything when leaving routing mode or starting over.
  const reset = useCallback(() => {
    setCandidates([]);
    setSelectedId(null);
    setStartPoint(null);
    setStartLabel(null);
    setPickingStart(false);
    setEndPoint(null);
    setEndLabel(null);
    setPickingEnd(false);
  }, []);

  return {
    distancePresets: DISTANCE_PRESETS,
    distanceKm,
    setDistanceKm,
    loop,
    setLoop,
    startPoint,
    setStartPoint,
    startLabel,
    setStartLabel,
    pickingStart,
    setPickingStart,
    endPoint,
    setEndPoint,
    endLabel,
    setEndLabel,
    pickingEnd,
    setPickingEnd,
    candidates,
    selectedId,
    setSelectedId,
    generate,
    isGenerating: isPending,
    reset,
  };
};
